<template>
  <div class="max-w-5xl mx-auto p-4 lg:p-6 bg-white shadow-md border border-iron rounded">
    <h1 class="text-2xl font-bold text-secondary mb-6 italic">Ficha Cadastral do Aluno</h1>

    <div class="mb-4">
      <img 
        v-if="student.photo"
        :src="student.className ? student.getPhoto() : student.photo"
        class="w-40 h-40 shadow-lg rounded-full ml-auto mr-auto md:ml-0 md:mr-auto"
      />
      <div class="mt-4">
        <FileUpload 
            mode="basic" 
            accept="image/*" 
            :auto="true"
            :maxFileSize="1000000" 
            @select="(e) => onPhotoUpload(e)"
            chooseLabel="Escolher Foto" 
            class="p-button-xs p-button-text"
            :invalidFileTypeMessage="translate('Invalid file type')"
            :invalidFileSizeMessage="translate('invalid file size, file size should be smaller than 1MB', 'ucfirst')"
          />
      </div>
      <small class="text-gray-500 text-center">Formatos aceitos: JPG, PNG. Máx 1MB.</small>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-12 gap-4">
      
      <div class="md:col-span-8 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary">Nome Completo</label>
        <InputText v-model="student.name" placeholder="Ex: Nickolas Bini" class="w-full" />
      </div>

      <div class="md:col-span-4 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary">Data de Nascimento</label>
        <DatePicker 
          v-model="student.birth_date" 
          showIcon 
          fluid 
          iconDisplay="input" 
          :dateFormat="getFormatByLanguage()"
        />
      </div>

      <div class="md:col-span-4 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary">Documento (Tipo & Número)</label>
          <div class="p-inputgroup">
            <Select 
              v-model="student.document_type" 
              :options="documentTypeOptions" 
              optionLabel="name" 
              placeholder="Tipo" 
              class="w-32"
            />

            <InputMask 
              v-if="currentMask"
              v-model="student.document" 
              :mask="currentMask" 
              :unmask="true" 
              placeholder="Número"
            />
            
            <InputText 
              v-else 
              v-model="student.document" 
              placeholder="Número" 
            />
          </div>
          <small v-if="student.document_type?.name" class="text-gray-400">
            Formato: {{ student.document_type.name }}
          </small>
      </div>

      <div class="md:col-span-4 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary">Nacionalidade</label>
        <InputText v-model="student.nacionality" placeholder="Ex: Brasileiro" />
      </div>

      <div class="md:col-span-4 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary">Ocupação</label>
        <InputText v-model="student.occupation" placeholder="Ex: Desenvolvedor" />
      </div>

      <div class="md:col-span-12 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary">Endereço Completo</label>
        <InputText v-model="student.address" placeholder="Rua, Número, Bairro, Cidade..." />
      </div>

      <div class="md:col-span-4 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary">Status</label>
        <Select v-model="student.status" :options="statusOptions" optionLabel="name" />
      </div>

      <hr class="md:col-span-12 my-4 border-iron" />

      <!-- Detalhes das Aulas -->
      <div class="md:col-span-4 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary text-carrot">Duração da Aula (Minutos)</label>
        <InputNumber v-model="student.duration_in_minutes" suffix=" min" showButtons :min="0" />
      </div>

      <div class="md:col-span-4 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary text-carrot">Frequência</label>
        <InputText v-model="student.class_frequency" placeholder="Ex: 2x por semana" />
      </div>

      <div class="md:col-span-4 flex flex-col gap-2">
        <label class="font-bold text-sm text-secondary text-carrot">Dia do Pagamento</label>
        <InputNumber v-model="student.payment_day" prefix="Dia " :min="1" :max="31" />
      </div>

      <!-- Gestor de Horários (JSON) -->
      <div class="md:col-span-12 bg-gray-50 p-4 rounded-lg border border-dashed border-iron">
        <div class="flex justify-between items-center mb-4">
          <label class="font-bold text-secondary">Grade de Horários</label>
          <Button label="Novo Horário" icon="pi pi-plus" @click="addSchedule" class="p-button-text p-button-sm" />
        </div>

        <div v-for="(schedule, index) in classSchedules" :key="index" class="flex flex-wrap gap-3 mb-3 items-end bg-white p-3 rounded shadow-sm">
          <div class="flex flex-col gap-1">
            <small class="font-bold">Dia</small>
            <Dropdown v-model="schedule.day" :options="daysOfWeek" placeholder="Selecione" class="w-40" />
          </div>
          <div class="flex flex-col gap-1">
            <small class="font-bold">Início</small>
            <InputText v-model="schedule.start" type="time" />
          </div>
          <div class="flex flex-col gap-1">
            <small class="font-bold">Fim</small>
            <InputText v-model="schedule.end" type="time" />
          </div>
          <Button icon="pi pi-trash" class="p-button-danger p-button-text" @click="removeSchedule(index)" />
        </div>
      </div>

      <!-- Botão de Salvar -->
      <div class="md:col-span-12 flex justify-end mt-6">
        <Button label="Salvar Aluno" icon="pi pi-save" @click="saveStudent" :loading="loading" class="bg-purple-suit-forced text-white px-8 py-3" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted, defineEmits, computed } from 'vue';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { translate } from '@/composables/translate';
import { DatePicker, FileUpload } from 'primevue';
import { showToast } from '@/composables/toast';
import { useContextStore } from '@/stores/context';
import InputMask from 'primevue/inputmask';
import StudentService from '@/services/StudentService';
import Select from 'primevue/select';
import Student from '@/api/models/Student';

const toast = useToast();
const loading = ref(false);
const contextStore = useContextStore();

const student = ref({
  name: '',
  birth_date: null,
  nacionality: '',
  occupation: '',
  document: '',
  document_type: '',
  address: '',
  class_frequency: '',
  duration_in_minutes: 60,
  payment_day: 1,
  photo: null,
  status: 1
});

const statusOptions = [
  {name: 'Ativo', code: 1}, 
  {name: 'Inativo', code: 0}
];
const documentTypeOptions = [
  {name: 'RG', code: 1, mask: '99.999.999-9'},
  {name: 'CPF', code: 2, mask: '999.999.999-99'},
  {name: 'CNPJ', code: 3, mask: '99.999.999/9999-99'},
  {name: 'Passaporte', code: 4, mask: null}
];

const props = defineProps(['studentData']);
const emit = defineEmits(['save', 'cancel']);
const currentMask = computed(() => {
  return student.value.document_type?.mask || null;
});

onMounted(() => {
  if (props.studentData) {
    student.value = new Student(props.studentData);

    classSchedules.value = student.value.getClassSchedule();
    student.value.birth_date = student.value.getBirthDate();

    if (student.value.status) {
      for (let status of statusOptions) {
        if (status.code == student.value.status) {
          student.value.status = status;
        }
      }
    }

    if (student.value.document_type) {
      for (let document_type of documentTypeOptions) {
        if (document_type.code == student.value.document_type) {
          student.value.document_type = document_type;
        }
      }
    }
  }
});

const classSchedules = ref([]);
const daysOfWeek = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado', 'Domingo'];

const addSchedule = () => classSchedules.value.push({ day: '', start: '', end: '' });
const removeSchedule = (index) => classSchedules.value.splice(index, 1);

const hasChangedPhoto = ref(false);
const onPhotoUpload = (event) => {
  try {
    const file = event.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      student.value.photo = e.target.result;
      hasChangedPhoto.value = true;
    };
    reader.readAsDataURL(file);
    
  } catch (error) {
    showToast(toast, translate('invalid file'), 'error');
  }
};

const saveStudent = async () => {
  if (!student.value.name) {
    showToast(toast, translate('the name is required'), 'error');
    return;
  }

  loading.value = true;

  let payload = {
    ...student.value,
    class_time: JSON.stringify(classSchedules.value)
  };

  if (!hasChangedPhoto.value) {
    payload.photo = null;
  }

  payload.status = payload.status ? payload.status.code : null;
  payload.document_type = payload.document_type ? payload.document_type.code : null;
  payload.birth_date = payload.birth_date && payload.birth_date instanceof Date 
    ? payload.birth_date.toISOString().split('T')[0]
    : null;

  const response = await StudentService.save(payload);

  if (!response.success()) {
    showToast(toast, response.getMessage(), 'error');
  }

  showToast(toast, translate('student saved'));
  emit('save');

  loading.value = false;
};

function getFormatByLanguage() {
    const lang = contextStore.getLang;
    return (lang === 'pt' || lang === 'es') ? 'dd/mm/yy' : 'yy/mm/dd';
}
</script>